
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import authSession from "../../lib/authSession";
import { invalidateAdminCache } from "../../lib/adminApiCache";
import { authInitialState, createAuthActions } from "./adminAuthStore";
import { dashboardInitialState, createDashboardActions } from "./adminDashboardStore";
import {
  resourcePersonInitialState,
  createResourcePersonActions,
} from "./adminResourcePersonStore";
import { staffInitialState, createStaffActions } from "./adminStaffStore";
import { examPinInitialState, createExamPinActions } from "./adminExamPinStore";
import {
  examPurchaseInitialState,
  createExamPurchaseActions,
} from "./adminExamPurchaseStore";
import { activityLogInitialState, createActivityLogActions } from "./adminActivityLogStore";
import { studentInitialState, createStudentActions } from "./adminStudentStore";
import { withdrawalInitialState, createWithdrawalActions } from "./adminWithdrawalStore";
import {
  academicResourceInitialState,
  createAcademicResourceActions,
} from "./adminAcademicResourceStore";
import {
  learningPathInitialState,
  createLearningPathActions,
} from "./adminLearningPathStore";
import {
  notificationInitialState,
  createNotificationActions,
} from "./adminNotificationStore";
import { newsInitialState, createNewsActions } from "./adminNewsStore";
import {
  marketplaceInitialState,
  createMarketplaceActions,
} from "./adminMarketplaceStore";
import { categoryInitialState, createCategoryActions } from "./adminCategoryStore";
import {
  departmentInitialState,
  createDepartmentActions,
} from "./adminDepartmentStore";
import { settingsInitialState, createSettingsActions } from "./adminSettingsStore";
import { ledgerInitialState, createLedgerActions } from "./adminLedgerStore";
import { paymentInitialState, createPaymentActions } from "./adminPaymentStore";
import {
  reconciliationInitialState,
  createReconciliationActions,
} from "./adminReconciliationStore";

/**
 * The single global admin store. Actions from every slice are spread onto it,
 * along with the required initial state and a small set of shared helpers.
 */
export const useAdminStore = create((set, get) => ({
  // Slice state
  ...authInitialState,
  ...dashboardInitialState,
  ...resourcePersonInitialState,
  ...staffInitialState,
  ...examPinInitialState,
  ...examPurchaseInitialState,
  ...activityLogInitialState,
  ...studentInitialState,
  ...withdrawalInitialState,
  ...academicResourceInitialState,
  ...learningPathInitialState,
  ...notificationInitialState,
  ...newsInitialState,
  ...marketplaceInitialState,
  ...categoryInitialState,
  ...departmentInitialState,
  ...settingsInitialState,
  ...ledgerInitialState,
  ...paymentInitialState,
  ...reconciliationInitialState,

  // Shared message state
  successMessage: null,
  errorMessage: null,

  // Slice actions
  ...createAuthActions(set, get),
  ...createDashboardActions(set, get),
  ...createResourcePersonActions(set, get),
  ...createStaffActions(set, get),
  ...createExamPinActions(set, get),
  ...createExamPurchaseActions(set, get),
  ...createActivityLogActions(set, get),
  ...createStudentActions(set, get),
  ...createWithdrawalActions(set, get),
  ...createAcademicResourceActions(set, get),
  ...createLearningPathActions(set, get),
  ...createNotificationActions(set, get),
  ...createNewsActions(set, get),
  ...createMarketplaceActions(set, get),
  ...createCategoryActions(set, get),
  ...createDepartmentActions(set, get),
  ...createSettingsActions(set, get),
  ...createLedgerActions(set, get),
  ...createPaymentActions(set, get),
  ...createReconciliationActions(set, get),

  // Shared helpers
  getToken: () => get().token || authSession.getToken(),
  clearMessage: () => set({ successMessage: null, errorMessage: null }),
}));

// ── Dedicated selector hooks (Zustand best practice) ─────────────────────────

/** Auth + login selectors. */
export const useAdminAuth = () =>
  useAdminStore(
    useShallow((s) => ({
      isAuthenticated: s.isAuthenticated,
      token: s.token,
      isAdmin: s.isAdmin,
      user: s.user,
      loginLoading: s.loginLoading,
      loginError: s.loginError,
      login: s.login,
      logout: s.logout,
    }))
  );

/** Dashboard selectors. */
export const useAdminDashboard = () =>
  useAdminStore(
    useShallow((s) => ({
      dashboard: s.dashboard,
      dashboardLoading: s.dashboardLoading,
      dashboardError: s.dashboardError,
      fetchDashboard: s.fetchDashboard,
      refreshLoading: s.refreshLoading,
      refreshError: s.refreshError,
      refreshSession: s.refreshSession,
    }))
  );

/** Resource person selectors. */
export const useAdminResourcePersons = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      resourcePersons: s.resourcePersons,
      resourcePersonsLoading: s.resourcePersonsLoading,
      resourcePersonsError: s.resourcePersonsError,
      resourcePersonsTotal: s.resourcePersonsTotal,
      resourcePersonsPages: s.resourcePersonsPages,
      fetchResourcePersons: s.fetchResourcePersons,
      // Single resource person
      resourcePersonDetail: s.resourcePersonDetail,
      resourcePersonDetailLoading: s.resourcePersonDetailLoading,
      resourcePersonDetailError: s.resourcePersonDetailError,
      fetchResourcePerson: s.fetchResourcePerson,
      // Mutations
      createResourcePerson: s.createResourcePerson,
      updateResourcePerson: s.updateResourcePerson,
      updateResourcePersonStatus: s.updateResourcePersonStatus,
      resourcePersonMutationLoading: s.resourcePersonMutationLoading,
      resourcePersonMutationError: s.resourcePersonMutationError,
      // Earnings / resources / students / withdrawals
      resourcePersonEarnings: s.resourcePersonEarnings,
      resourcePersonEarningsLoading: s.resourcePersonEarningsLoading,
      fetchResourcePersonEarnings: s.fetchResourcePersonEarnings,
      resourcePersonResources: s.resourcePersonResources,
      resourcePersonResourcesLoading: s.resourcePersonResourcesLoading,
      resourcePersonResourcesData: s.resourcePersonResourcesData,
      fetchResourcePersonResources: s.fetchResourcePersonResources,
      resourcePersonStudents: s.resourcePersonStudents,
      resourcePersonStudentsLoading: s.resourcePersonStudentsLoading,
      fetchResourcePersonStudents: s.fetchResourcePersonStudents,
    }))
  );

/** Staff selectors. */
export const useAdminStaff = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      staff: s.staff,
      staffLoading: s.staffLoading,
      staffError: s.staffError,
      staffTotal: s.staffTotal,
      staffPages: s.staffPages,
      fetchStaff: s.fetchStaff,
      // Single staff member
      staffDetail: s.staffDetail,
      staffDetailLoading: s.staffDetailLoading,
      staffDetailError: s.staffDetailError,
      fetchStaffMember: s.fetchStaffMember,
      // Activity log
      staffActivity: s.staffActivity,
      staffActivityLoading: s.staffActivityLoading,
      staffActivityError: s.staffActivityError,
      staffActivityTotal: s.staffActivityTotal,
      staffActivityPages: s.staffActivityPages,
      fetchStaffActivity: s.fetchStaffActivity,
      // Mutations
      createStaff: s.createStaff,
      updateStaff: s.updateStaff,
      updateStaffStatus: s.updateStaffStatus,
      deleteStaff: s.deleteStaff,
      staffMutationLoading: s.staffMutationLoading,
      staffMutationError: s.staffMutationError,
        }))
  );

/** Exam Pins selectors. */
export const useAdminExamPins = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      examPins: s.examPins,
      examPinsLoading: s.examPinsLoading,
      examPinsError: s.examPinsError,
      examPinTotal: s.examPinTotal,
      examPinPages: s.examPinPages,
      fetchExamPins: s.fetchExamPins,
      // Single exam pin
      examPinDetail: s.examPinDetail,
      examPinDetailLoading: s.examPinDetailLoading,
      examPinDetailError: s.examPinDetailError,
      fetchExamPinById: s.fetchExamPinById,
      // Deliveries
      examPinDeliveries: s.examPinDeliveries,
      examPinDeliveriesLoading: s.examPinDeliveriesLoading,
      examPinDeliveriesError: s.examPinDeliveriesError,
      examPinDeliveryTotal: s.examPinDeliveryTotal,
      examPinDeliveryPages: s.examPinDeliveryPages,
      fetchExamPinDeliveries: s.fetchExamPinDeliveries,
      // Stats
      examPinStats: s.examPinStats,
      examPinStatsLoading: s.examPinStatsLoading,
      examPinStatsError: s.examPinStatsError,
      fetchExamPinStats: s.fetchExamPinStats,
      // Price
      examPinPrice: s.examPinPrice,
      examPinPriceLoading: s.examPinPriceLoading,
      examPinPriceError: s.examPinPriceError,
      fetchExamPinPrice: s.fetchExamPinPrice,
      updateExamPinPrice: s.updateExamPinPrice,
      // Mutations
      createExamPin: s.createExamPin,
      deleteExamPin: s.deleteExamPin,
      examPinMutationLoading: s.examPinMutationLoading,
      examPinMutationError: s.examPinMutationError,
    }))
  );

/** Exam Purchases selectors. */
export const useAdminExamPurchases = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      examPurchases: s.examPurchases,
      examPurchasesLoading: s.examPurchasesLoading,
      examPurchasesError: s.examPurchasesError,
      examPurchasesTotal: s.examPurchasesTotal,
      examPurchasesPages: s.examPurchasesPages,
      fetchExamPurchases: s.fetchExamPurchases,
      // Single purchase
      examPurchaseDetail: s.examPurchaseDetail,
      examPurchaseDetailLoading: s.examPurchaseDetailLoading,
      examPurchaseDetailError: s.examPurchaseDetailError,
      fetchExamPurchaseById: s.fetchExamPurchaseById,
      // Retry fulfillment
      retryExamPurchaseFulfillment: s.retryExamPurchaseFulfillment,
      // Stats
      examPurchaseStats: s.examPurchaseStats,
      examPurchaseStatsLoading: s.examPurchaseStatsLoading,
      examPurchaseStatsError: s.examPurchaseStatsError,
      fetchExamPurchaseStats: s.fetchExamPurchaseStats,
      // Dashboard
      examDashboard: s.examDashboard,
      examDashboardLoading: s.examDashboardLoading,
      examDashboardError: s.examDashboardError,
      fetchExamDashboard: s.fetchExamDashboard,
      // Mutations
      examPurchaseMutationLoading: s.examPurchaseMutationLoading,
      examPurchaseMutationError: s.examPurchaseMutationError,
    }))
  );

/** Student selectors. */
export const useAdminStudents = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      students: s.students,
      studentsLoading: s.studentsLoading,
      studentsError: s.studentsError,
      studentsTotal: s.studentsTotal,
      studentsPages: s.studentsPages,
      fetchStudents: s.fetchStudents,
      // Single student
      studentDetail: s.studentDetail,
      studentDetailLoading: s.studentDetailLoading,
      studentDetailError: s.studentDetailError,
      fetchStudent: s.fetchStudent,
      // Purchases
      studentPurchases: s.studentPurchases,
      studentPurchasesLoading: s.studentPurchasesLoading,
      studentPurchasesError: s.studentPurchasesError,
      fetchStudentPurchases: s.fetchStudentPurchases,
      // Subscriptions
      studentSubscriptions: s.studentSubscriptions,
      studentSubscriptionsLoading: s.studentSubscriptionsLoading,
      studentSubscriptionsError: s.studentSubscriptionsError,
      fetchStudentSubscriptions: s.fetchStudentSubscriptions,
      // Learning progress
      studentProgress: s.studentProgress,
      studentProgressLoading: s.studentProgressLoading,
      studentProgressError: s.studentProgressError,
      fetchStudentLearningProgress: s.fetchStudentLearningProgress,
      // Status update
      statusUpdateLoading: s.statusUpdateLoading,
      statusUpdateError: s.statusUpdateError,
      updateStudentStatus: s.updateStudentStatus,
    }))
    );

/** Withdrawal selectors. */
export const useAdminWithdrawals = () =>
  useAdminStore(
    useShallow((s) => ({
      withdrawals: s.withdrawals,
      withdrawalsLoading: s.withdrawalsLoading,
      withdrawalsError: s.withdrawalsError,
      withdrawalsTotal: s.withdrawalsTotal,
      withdrawalsPages: s.withdrawalsPages,
      withdrawalDetail: s.withdrawalDetail,
      withdrawalDetailLoading: s.withdrawalDetailLoading,
      withdrawalDetailError: s.withdrawalDetailError,
      withdrawalMutationLoading: s.withdrawalMutationLoading,
      withdrawalMutationError: s.withdrawalMutationError,
      fetchWithdrawals: s.fetchWithdrawals,
      fetchWithdrawalById: s.fetchWithdrawalById,
      approveWithdrawal: s.approveWithdrawal,
      rejectWithdrawal: s.rejectWithdrawal,
    }))
  );

/** Activity log selectors. */
export const useAdminActivityLogs = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      activityLogs: s.activityLogs,
      activityLogsLoading: s.activityLogsLoading,
      activityLogsError: s.activityLogsError,
      activityLogTotal: s.activityLogTotal,
      activityLogPages: s.activityLogPages,
      fetchActivityLogs: s.fetchActivityLogs,
      // Single activity log
      activityLogDetail: s.activityLogDetail,
      activityLogDetailLoading: s.activityLogDetailLoading,
      activityLogDetailError: s.activityLogDetailError,
      fetchActivityLogById: s.fetchActivityLogById,
    }))
  );

/** Academic Resources selectors (books / courses / journals). */
export const useAdminAcademicResources = () =>
  useAdminStore(
    useShallow((s) => ({
      // Books
      books: s.books,
      booksLoading: s.booksLoading,
      booksError: s.booksError,
      booksTotal: s.booksTotal,
      booksPages: s.booksPages,
      fetchBooks: s.fetchBooks,
      bookDetail: s.bookDetail,
      bookDetailLoading: s.bookDetailLoading,
      bookDetailError: s.bookDetailError,
      fetchBook: s.fetchBook,
      updateBook: s.updateBook,
      approveBook: s.approveBook,
      rejectBook: s.rejectBook,
      // Courses
      courses: s.courses,
      coursesLoading: s.coursesLoading,
      coursesError: s.coursesError,
      coursesTotal: s.coursesTotal,
      coursesPages: s.coursesPages,
      fetchCourses: s.fetchCourses,
      courseDetail: s.courseDetail,
      courseDetailLoading: s.courseDetailLoading,
      courseDetailError: s.courseDetailError,
      fetchCourse: s.fetchCourse,
      updateCourse: s.updateCourse,
      approveCourse: s.approveCourse,
      rejectCourse: s.rejectCourse,
      disableCourse: s.disableCourse,
      featureCourse: s.featureCourse,
      unfeatureCourse: s.unfeatureCourse,
      // Journals
      journals: s.journals,
      journalsLoading: s.journalsLoading,
      journalsError: s.journalsError,
      journalsTotal: s.journalsTotal,
      journalsPages: s.journalsPages,
      fetchJournals: s.fetchJournals,
      journalDetail: s.journalDetail,
      journalDetailLoading: s.journalDetailLoading,
      journalDetailError: s.journalDetailError,
      fetchJournal: s.fetchJournal,
      updateJournal: s.updateJournal,
      approveJournal: s.approveJournal,
      rejectJournal: s.rejectJournal,
      // Pending moderation badge
      pendingModerationCount: s.pendingModerationCount,
      pendingModerationLoading: s.pendingModerationLoading,
      fetchPendingModerationCount: s.fetchPendingModerationCount,
      // Mutations
      academicMutationLoading: s.academicMutationLoading,
      academicMutationError: s.academicMutationError,
    }))
  );

/** Learning Path moderation selectors. */
export const useAdminLearningPaths = () =>
  useAdminStore(
    useShallow((s) => ({
      // List / moderation queue
      learningPaths: s.learningPaths,
      learningPathsLoading: s.learningPathsLoading,
      learningPathsError: s.learningPathsError,
      learningPathsTotal: s.learningPathsTotal,
      learningPathsPages: s.learningPathsPages,
      fetchLearningPaths: s.fetchLearningPaths,
      // Single learning path
      learningPathDetail: s.learningPathDetail,
      learningPathDetailLoading: s.learningPathDetailLoading,
      learningPathDetailError: s.learningPathDetailError,
      fetchLearningPathById: s.fetchLearningPathById,
      // Moderation
      learningPathMutationLoading: s.learningPathMutationLoading,
      learningPathMutationError: s.learningPathMutationError,
      updateLearningPath: s.updateLearningPath,
      approveLearningPath: s.approveLearningPath,
      rejectLearningPath: s.rejectLearningPath,
    }))
  );

/** Notifications / dead-letter selectors. */
export const useAdminNotifications = () =>
  useAdminStore(
    useShallow((s) => ({
      // Notifications
      notifications: s.notifications,
      notificationsLoading: s.notificationsLoading,
      notificationsError: s.notificationsError,
      notificationsTotal: s.notificationsTotal,
      notificationsPages: s.notificationsPages,
      fetchNotifications: s.fetchNotifications,
      notificationDetail: s.notificationDetail,
      notificationDetailLoading: s.notificationDetailLoading,
      notificationDetailError: s.notificationDetailError,
      fetchNotification: s.fetchNotification,
      markNotificationRead: s.markNotificationRead,
      markAllNotificationsRead: s.markAllNotificationsRead,
      unreadCount: s.unreadCount,
      unreadCountLoading: s.unreadCountLoading,
      unreadCountError: s.unreadCountError,
      fetchUnreadNotificationCount: s.fetchUnreadNotificationCount,
      // Dead letters
      deadLetters: s.deadLetters,
      deadLettersLoading: s.deadLettersLoading,
      deadLettersError: s.deadLettersError,
      deadLettersTotal: s.deadLettersTotal,
      notificationMutationLoading: s.notificationMutationLoading,
      notificationMutationError: s.notificationMutationError,
      fetchDeadLetters: s.fetchDeadLetters,
      replayDeadLetter: s.replayDeadLetter,
    }))
  );

/** News selectors (articles + categories). */
export const useAdminNews = () =>
  useAdminStore(
    useShallow((s) => ({
      // List
      news: s.news,
      newsLoading: s.newsLoading,
      newsError: s.newsError,
      newsTotal: s.newsTotal,
      newsPages: s.newsPages,
      fetchNews: s.fetchNews,
      // Single article
      newsDetail: s.newsDetail,
      newsDetailLoading: s.newsDetailLoading,
      newsDetailError: s.newsDetailError,
      fetchNewsById: s.fetchNewsById,
      // Mutations
      createNews: s.createNews,
      uploadNewsImage: s.uploadNewsImage,
      updateNews: s.updateNews,
      deleteNewsById: s.deleteNewsById,
      publishNews: s.publishNews,
      unpublishNews: s.unpublishNews,
      featureNews: s.featureNews,
      unfeatureNews: s.unfeatureNews,
      // Categories
      newsCategories: s.newsCategories,
      newsCategoriesLoading: s.newsCategoriesLoading,
      newsCategoriesError: s.newsCategoriesError,
      fetchNewsCategories: s.fetchNewsCategories,
      createNewsCategory: s.createNewsCategory,
      updateNewsCategory: s.updateNewsCategory,
      deleteNewsCategory: s.deleteNewsCategory,
      // Mutation flags
      newsMutationLoading: s.newsMutationLoading,
      newsMutationError: s.newsMutationError,
    }))
  );

/** Marketplace selectors (purchases + resource moderation). */
export const useAdminMarketplace = () =>
  useAdminStore(
    useShallow((s) => ({
      // Purchases / revenue
      marketplacePurchases: s.marketplacePurchases,
      marketplacePurchasesLoading: s.marketplacePurchasesLoading,
      marketplacePurchasesError: s.marketplacePurchasesError,
      marketplacePurchasesTotal: s.marketplacePurchasesTotal,
      marketplacePurchasesPages: s.marketplacePurchasesPages,
      fetchMarketplacePurchases: s.fetchMarketplacePurchases,
      revokeMarketplacePurchase: s.revokeMarketplacePurchase,
      restoreMarketplacePurchase: s.restoreMarketplacePurchase,
      // Moderation counts
      marketplaceModerationCounts: s.marketplaceModerationCounts,
      fetchMarketplaceModerationCounts: s.fetchMarketplaceModerationCounts,
      // Resources
      marketplaceResources: s.marketplaceResources,
      marketplaceResourcesType: s.marketplaceResourcesType,
      marketplaceResourcesLoading: s.marketplaceResourcesLoading,
      marketplaceResourcesError: s.marketplaceResourcesError,
      marketplaceResourcesTotal: s.marketplaceResourcesTotal,
      marketplaceResourcesPages: s.marketplaceResourcesPages,
      fetchMarketplaceResources: s.fetchMarketplaceResources,
      marketplaceResourceDetail: s.marketplaceResourceDetail,
      marketplaceResourceDetailLoading: s.marketplaceResourceDetailLoading,
      marketplaceResourceDetailError: s.marketplaceResourceDetailError,
      fetchMarketplaceResource: s.fetchMarketplaceResource,
      updateMarketplaceResource: s.updateMarketplaceResource,
      approveMarketplaceResource: s.approveMarketplaceResource,
      rejectMarketplaceResource: s.rejectMarketplaceResource,
      restoreMarketplaceResource: s.restoreMarketplaceResource,
      withdrawMarketplaceResource: s.withdrawMarketplaceResource,
      // Mutation flags
      marketplaceMutationLoading: s.marketplaceMutationLoading,
      marketplaceMutationError: s.marketplaceMutationError,
    }))
  );

/** Course categories selectors. */
export const useAdminCategories = () =>
  useAdminStore(
    useShallow((s) => ({
      categories: s.categories,
      categoriesLoading: s.categoriesLoading,
      categoriesError: s.categoriesError,
      categoryDetail: s.categoryDetail,
      categoryDetailLoading: s.categoryDetailLoading,
      categoryDetailError: s.categoryDetailError,
      categoryMutationLoading: s.categoryMutationLoading,
      categoryMutationError: s.categoryMutationError,
      fetchCategories: s.fetchCategories,
      fetchCategory: s.fetchCategory,
      createCategory: s.createCategory,
      updateCategory: s.updateCategory,
      deleteCategory: s.deleteCategory,
    }))
  );

/** Departments selectors. */
export const useAdminDepartments = () =>
  useAdminStore(
    useShallow((s) => ({
      departments: s.departments,
      departmentsLoading: s.departmentsLoading,
      departmentsError: s.departmentsError,
      departmentMutationLoading: s.departmentMutationLoading,
      departmentMutationError: s.departmentMutationError,
      fetchDepartments: s.fetchDepartments,
      createDepartment: s.createDepartment,
      updateDepartment: s.updateDepartment,
      deleteDepartment: s.deleteDepartment,
    }))
  );

/** Platform settings selectors. */
export const useAdminSettings = () =>
  useAdminStore(
    useShallow((s) => ({
      settings: s.settings,
      settingsLoading: s.settingsLoading,
      settingsError: s.settingsError,
      settingsMutationLoading: s.settingsMutationLoading,
      settingsMutationError: s.settingsMutationError,
      fetchSettings: s.fetchSettings,
      updateSettings: s.updateSettings,
    }))
  );

/** Resource-person ledger selectors. */
export const useAdminLedger = () =>
  useAdminStore(
    useShallow((s) => ({
      ledgerEntries: s.ledgerEntries,
      ledgerLoading: s.ledgerLoading,
      ledgerError: s.ledgerError,
      ledgerTotal: s.ledgerTotal,
      ledgerPages: s.ledgerPages,
      fetchResourcePersonLedger: s.fetchResourcePersonLedger,
    }))
  );

/** Admin payment verification selectors. */
export const useAdminPayments = () =>
  useAdminStore(
    useShallow((s) => ({
      paymentVerifyLoading: s.paymentVerifyLoading,
      paymentVerifyError: s.paymentVerifyError,
      verifiedTransaction: s.verifiedTransaction,
      adminVerifyPayment: s.adminVerifyPayment,
    }))
  );

/** Reconciliation selectors. */
export const useAdminReconciliation = () =>
  useAdminStore(
    useShallow((s) => ({
      reconciliationIssues: s.reconciliationIssues,
      reconciliationLoading: s.reconciliationLoading,
      reconciliationError: s.reconciliationError,
      reconciliationRun: s.reconciliationRun,
      reconciliationMutationLoading: s.reconciliationMutationLoading,
      reconciliationMutationError: s.reconciliationMutationError,
      fetchReconciliationIssues: s.fetchReconciliationIssues,
      runReconciliation: s.runReconciliation,
    }))
  );

/** Generic messages + token helpers. */
export const useAdminMessages = () =>
  useAdminStore(
    useShallow((s) => ({
      successMessage: s.successMessage,
      errorMessage: s.errorMessage,
      clearMessage: s.clearMessage,
      getToken: s.getToken,
    }))
  );

/**
 * Any successful admin mutation makes the dashboard numbers stale, so drop the
 * cached dashboard snapshots. The next visit then revalidates instead of showing
 * outdated figures. (Mutations signal success via the shared `successMessage`.)
 */
useAdminStore.subscribe((state, prevState) => {
  const mutated =
    Boolean(state.successMessage) && state.successMessage !== prevState.successMessage;
  if (!mutated) return;

  // Drop every cached admin GET response so lists/pages revalidate.
  invalidateAdminCache();

  if (state.dashboardFetchedAt) {
    useAdminStore.setState({ dashboardFetchedAt: null });
  }
  if (state.examDashboardCache && Object.keys(state.examDashboardCache).length > 0) {
    useAdminStore.setState({ examDashboardCache: {} });
  }
});

// Backwards-compatible alias for the combined action hook.
export const useAdminActions = useAdminAuth;

export default useAdminStore;
